import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ArrowRight, FileText } from 'lucide-react';
import SEO from '../components/SEO';
import { apiRequest } from '../lib/utils';
import { BLOG_CATEGORIES, formatBlogDate, authorName } from '../constants/blog';

export const BlogHeader = () => (
  <header className="border-b border-slate-100 bg-white/90 backdrop-blur sticky top-0 z-30">
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
      <Link to="/" data-testid="blog-logo-link"><span className="text-xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>Physign</span></Link>
      <nav className="flex items-center gap-6 text-sm">
        <Link to="/blog" className="text-slate-600 hover:text-[#3B1C73] hidden sm:inline">Blog</Link>
        <Link to="/search" className="text-slate-600 hover:text-[#3B1C73] hidden sm:inline">Find Doctors</Link>
        <Link to="/auth" className="bg-[#3B1C73] text-white px-4 py-2 rounded-full font-medium hover:bg-[#2c1457]">Login / Sign Up</Link>
      </nav>
    </div>
  </header>
);

const BlogCard = ({ blog, featured }) => (
  <Link to={`/blog/${blog.slug}`} data-testid={`blog-card-${blog.slug}`}
    className={`group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 flex flex-col ${featured ? 'md:col-span-2 md:flex-row' : ''}`}>
    <div className={`bg-purple-50 overflow-hidden flex items-center justify-center ${featured ? 'md:w-1/2 h-56 md:h-auto' : 'h-48'}`}>
      {blog.cover_image ? <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <FileText className="w-10 h-10 text-purple-200" />}
    </div>
    <div className={`p-5 flex flex-col flex-1 ${featured ? 'md:p-8 md:justify-center' : ''}`}>
      {blog.category && <span className="text-xs font-semibold uppercase tracking-wider text-[#3B1C73]">{blog.category}</span>}
      <h2 className={`font-bold text-slate-900 mt-2 group-hover:text-[#3B1C73] transition-colors ${featured ? 'text-2xl md:text-3xl' : 'text-lg'}`}>{blog.title}</h2>
      {blog.excerpt && <p className="text-sm text-slate-600 mt-2 line-clamp-3">{blog.excerpt}</p>}
      <p className="text-xs text-slate-400 mt-auto pt-4">{authorName(blog.author_name, blog.author_role)} · {formatBlogDate(blog.published_at)}</p>
    </div>
  </Link>
);

export default function Blog() {
  const [params, setParams] = useSearchParams();
  const category = params.get('category') || '';
  const [q, setQ] = useState(params.get('q') || '');
  const [data, setData] = useState({ blogs: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams({ page, limit: 9, ...(category && { category }), ...(params.get('q') && { q: params.get('q') }) });
    apiRequest(`/blogs?${qs}`)
      .then((d) => setData((prev) => ({ total: d.total, blogs: page === 1 ? d.blogs : [...prev.blogs, ...d.blogs] })))
      .catch(() => setData({ blogs: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [category, params, page]);

  const update = (next) => { setPage(1); setParams(Object.fromEntries(Object.entries({ category, q: params.get('q') || '', ...next }).filter(([, v]) => v))); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-page">
      <SEO path="/blog" title="Health Blog - Tips & Articles from Doctors" description="Read health tips, guides and articles written by Physign doctors on diabetes, physiotherapy, home care, nutrition and more." />
      <BlogHeader />
      <section className="bg-white border-b border-slate-100 px-6 pt-14 pb-8">
        <div className="max-w-7xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#3B1C73]">Physign Health Blog</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mt-2 max-w-3xl leading-tight">Health tips &amp; guides from our doctors</h1>
          <form onSubmit={(e) => { e.preventDefault(); update({ q }); }} className="mt-8 flex max-w-xl bg-slate-50 border border-slate-200 rounded-full overflow-hidden">
            <Search className="w-5 h-5 text-slate-400 ml-4 self-center flex-shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles" className="flex-1 bg-transparent px-3 py-3 outline-none text-sm" data-testid="blog-search-input" />
            <button className="bg-[#3B1C73] text-white px-5 text-sm font-medium" data-testid="blog-search-btn">Search</button>
          </form>
          <div className="flex gap-2 mt-6 overflow-x-auto pb-2 -mx-1 px-1">
            {['', ...BLOG_CATEGORIES].map((c) => (
              <button key={c || 'all'} onClick={() => update({ category: c })} data-testid={`blog-category-${c || 'all'}`}
                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm transition-colors ${category === c ? 'bg-[#3B1C73] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {c || 'All'}
              </button>
            ))}
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {loading && page === 1 ? (
          <div className="py-20 flex justify-center"><div className="spinner" /></div>
        ) : data.blogs.length === 0 ? (
          <div className="text-center py-20" data-testid="blog-list-empty">
            <FileText className="w-12 h-12 text-slate-300 mx-auto" />
            <p className="text-slate-500 mt-3">No articles found yet. Check back soon!</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list">
              {data.blogs.map((b, i) => <BlogCard key={b.id} blog={b} featured={i === 0 && page === 1 && !category && !params.get('q') && !!b.cover_image} />)}
            </div>
            {data.blogs.length < data.total && (
              <div className="text-center mt-10">
                <button onClick={() => setPage((p) => p + 1)} disabled={loading} className="inline-flex items-center gap-2 border-2 border-[#3B1C73] text-[#3B1C73] px-6 py-2.5 rounded-full font-semibold hover:bg-purple-50" data-testid="blog-load-more">
                  {loading ? 'Loading…' : 'Load more'} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
